import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Code2,
  Cog,
  Database,
  Lock,
  Workflow,
  Zap,
  CheckCircle,
  ArrowRight,
  Check,
  X,
  GitBranch,
  FileCode,
  GraduationCap,
  Building,
  Stethoscope,
  Factory,
  Truck,
  ShoppingBag,
} from "lucide-react";
import "./CustomSoftwareDevelopment.css";
import { subscribeToNewsletter } from "./newsletterService";

export default function CustomSoftwareDevelopment() {
  const [activeTab, setActiveTab] = useState("languages");
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const fallbackPalette = ["#059669", "#0ea5e9", "#6366f1", "#f97316", "#ef4444", "#14b8a6"];

  const getInitials = (name) =>
    name
      .split(/\s+/)
      .map((word) => word[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  const makeFallbackSvg = (label, color) => {
    const safeLabel = getInitials(label);
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
        <defs>
          <linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stop-color="${color}" stop-opacity="0.9" />
            <stop offset="100%" stop-color="${color}" stop-opacity="0.7" />
          </linearGradient>
        </defs>
        <rect x="4" y="4" width="56" height="56" rx="14" fill="url(#g)" />
        <text x="32" y="38" text-anchor="middle" font-family="Arial, sans-serif" font-size="20" font-weight="700" fill="#ffffff">
          ${safeLabel}
        </text>
      </svg>
    `;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  };

  const startProject = () => {
    window.location.href = "/#contact";
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const email = form.newsletterEmail?.value || "";
    const result = subscribeToNewsletter(email);
    window.alert(result.message);
    if (result.ok) {
      form.reset();
    }
  };

  

  const technologies = {
    languages: [
      {
        name: "Python",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
        color: "#3776AB",
      },
      {
        name: "Java",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg",
        color: "#007396",
      },
      {
        name: "C#",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/csharp/csharp-original.svg",
        color: "#239120",
      },
      {
        name: "Go",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/go/go-original.svg",
        color: "#00ADD8",
      },
      {
        name: "Rust",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/rust/rust-original.svg",
        color: "#000000",
      },
      {
        name: "C++",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cplusplus/cplusplus-original.svg",
        color: "#00599C",
      },
    ],
    frameworks: [
      {
        name: "Django",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/django/django-plain.svg",
        color: "#092E20",
      },
      {
        name: ".NET",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/dot-net/dot-net-original.svg",
        color: "#512BD4",
      },
      {
        name: "Spring",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/spring/spring-original.svg",
        color: "#6DB33F",
      },
      {
        name: "FastAPI",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/fastapi/fastapi-original.svg",
        color: "#009688",
      },
      {
        name: "Flask",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/flask/flask-original.svg",
        color: "#000000",
      },
      {
        name: "Express",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg",
        color: "#000000",
      },
    ],
    tools: [
      {
        name: "Docker",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg",
        color: "#2496ED",
      },
      {
        name: "Kubernetes",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg",
        color: "#326CE5",
      },
      {
        name: "Jenkins",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jenkins/jenkins-original.svg",
        color: "#D24939",
      },
      {
        name: "Git",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg",
        color: "#F05032",
      },
      {
        name: "Redis",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redis/redis-original.svg",
        color: "#DC382D",
      },
      {
        name: "RabbitMQ",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/rabbitmq/rabbitmq-original.svg",
        color: "#FF6600",
      },
    ],
  };

  const whatWeOffer = [
    {
      icon: <Code2 className="w-6 h-6" />,
      title: "Enterprise Software",
      description:
        "Scalable business solutions including ERP, CRM, and workflow automation systems tailored to your operations.",
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Data Management Systems",
      description:
        "Custom databases, data warehouses, and analytics platforms for handling complex data requirements.",
    },
    {
      icon: <Workflow className="w-6 h-6" />,
      title: "Process Automation",
      description:
        "Streamline operations with automated workflows, reducing manual work and increasing efficiency.",
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "Security Solutions",
      description:
        "Custom security software including authentication systems, encryption tools, and compliance solutions.",
    },
    {
      icon: <GitBranch className="w-6 h-6" />,
      title: "Integration Solutions",
      description:
        "Connect disparate systems, legacy software, and third-party services into unified ecosystems.",
    },
    {
      icon: <FileCode className="w-6 h-6" />,
      title: "Legacy Modernization",
      description:
        "Modernize outdated systems using current technologies while preserving core business logic.",
    },
  ];

  const whyChooseUs = [
    "Bespoke solutions built for your workflow",
    "Scalable architecture for future growth",
    "Comprehensive documentation",
    "Source code ownership included",
    "Agile development with regular demos",
    "DevOps and CI/CD implementation",
    "Performance optimization",
    "Long-term maintenance support",
  ];

  const packages = [
    {
      name: "Small Project",
      price: "From R20,000",
      ideal: "Simple automation & tools",
      features: [
        { text: "Single application/tool", included: true },
        { text: "Basic architecture", included: true },
        { text: "Standard integrations", included: true },
        { text: "Documentation included", included: true },
        { text: "3 months support", included: true },
        { text: "Microservices architecture", included: false },
        { text: "Advanced integrations", included: false },
        { text: "Dedicated DevOps", included: false },
      ],
      popular: false,
    },
    {
      name: "Medium Project",
      price: "From R50,000",
      ideal: "Business management systems",
      features: [
        { text: "Multi-module system", included: true },
        { text: "Scalable architecture", included: true },
        { text: "Multiple integrations", included: true },
        { text: "Full documentation", included: true },
        { text: "6 months support", included: true },
        { text: "Database optimization", included: true },
        { text: "API development", included: true },
        { text: "Security audit", included: true },
      ],
      popular: true,
    },
    {
      name: "Enterprise",
      price: "From R150,000",
      ideal: "Complex enterprise systems",
      features: [
        { text: "Full enterprise solution", included: true },
        { text: "Microservices architecture", included: true },
        { text: "Unlimited integrations", included: true },
        { text: "Technical documentation", included: true },
        { text: "12+ months support", included: true },
        { text: "High availability setup", included: true },
        { text: "Performance monitoring", included: true },
        { text: "Dedicated support team", included: true },
      ],
      popular: false,
    },
  ];

  const process = [
    {
      step: "1",
      title: "Requirements Analysis",
      description:
        "Deep dive into your business processes, pain points, and goals to define exact requirements.",
    },
    {
      step: "2",
      title: "Architecture Design",
      description:
        "Create system architecture, data models, and technical specifications for scalable solutions.",
    },
    {
      step: "3",
      title: "Development",
      description:
        "Agile development sprints with regular demos, code reviews, and continuous integration.",
    },
    {
      step: "4",
      title: "Testing & QA",
      description:
        "Comprehensive testing including unit, integration, security, and performance testing.",
    },
    {
      step: "5",
      title: "Deployment & Training",
      description:
        "Production deployment, team training, documentation handover, and ongoing support.",
    },
  ];

  // Updated industries with professional icons
  const industries = [
    {
      name: "Finance & Banking",
      icon: <Building className="w-8 h-8" />,
      description: "Trading platforms, payment systems, risk management tools",
    },
    {
      name: "Healthcare",
      icon: <Stethoscope className="w-8 h-8" />,
      description: "Patient management, telemedicine, medical records systems",
    },
    {
      name: "Manufacturing",
      icon: <Factory className="w-8 h-8" />,
      description:
        "Inventory management, supply chain, quality control systems",
    },
    {
      name: "Logistics",
      icon: <Truck className="w-8 h-8" />,
      description: "Fleet management, route optimization, warehouse systems",
    },
    {
      name: "Education",
      icon: <GraduationCap className="w-8 h-8" />,
      description:
        "Learning management, student portals, administrative systems",
    },
    {
      name: "Retail",
      icon: <ShoppingBag className="w-8 h-8" />,
      description: "POS systems, inventory, customer relationship management",
    },
  ];

  const benefits = [
    {
      title: "Perfect Fit",
      description:
        "Software designed exactly for your unique business processes, not forced to adapt to generic solutions.",
      icon: <Cog className="w-8 h-8" />,
    },
    {
      title: "Competitive Advantage",
      description:
        "Proprietary software gives you capabilities competitors using off-the-shelf solutions can't match.",
      icon: <Zap className="w-8 h-8" />,
    },
    {
      title: "Full Control",
      description:
        "Own the source code, control updates, and modify functionality as your business evolves.",
      icon: <Lock className="w-8 h-8" />,
    },
    {
      title: "Scalability",
      description:
        "Built to grow with your business without the limitations of third-party software.",
      icon: <Database className="w-8 h-8" />,
    },
  ];

  const projectShowcase = [
    {
      src: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=80",
      title: "Business-Critical Systems",
      tag: "Enterprise",
    },
    {
      src: "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1200&q=80",
      title: "Workflow Automation Platforms",
      tag: "Operations",
    },
    {
      src: "https://images.unsplash.com/photo-1531403009284-440f080d1e12?auto=format&fit=crop&w=1200&q=80",
      title: "Secure Data-Driven Products",
      tag: "Security + Scale",
    },
  ];

  return (
    <div className="custom-software-page">
     <nav className={`navbar ${isScrolled ? "scrolled" : ""}`}>
        <div className="container navbar-container">
          <a href="/" className="navbar-brand">
            <img src="/TechXM-logo.png" alt="TECHXM logo" className="brand-logo-mark" width="30" height="30" />
          </a>

          <div className={`navbar-menu ${mobileMenuOpen ? "active" : ""}`}>
            <div className="services-dropdown">
              <div className="navbar-link active-link">Services</div>
              <div className="dropdown-menu">
                <Link to="/web-development" className="dropdown-item">
                  <i className="fas fa-laptop-code"></i>
                  Web Development Services
                </Link>
                <Link to="/mobile-app" className="dropdown-item">
                  <i className="fas fa-mobile-alt"></i>
                  Mobile App Development
                </Link>
                <Link to="/custom-software" className="dropdown-item active-link">
                  <i className="fas fa-cogs"></i>
                  Custom Software Development
                </Link>
                <Link to="/api-backend" className="dropdown-item">
                  <i className="fas fa-plug"></i>
                  API & Backend Services
                </Link>
                <Link to="/maintenance" className="dropdown-item">
                  <i className="fas fa-tools"></i>
                  Website & App Maintenance
                </Link>
                <Link to="/ai-services" className="dropdown-item">
                  <i className="fas fa-robot"></i>
                  AI & Machine Learning Services
                </Link>
              </div>
            </div>
            {/* <a href="/#work" className="navbar-link">Work</a> */}
            <a href="/#about" className="navbar-link">
              About
            </a>
{/*
            <a href="/#testimonials" className="navbar-link">
              Testimonials
            </a>
*/}
            <a href="/#faq" className="navbar-link">
              FAQ
            </a>
            <a href="/#contact" className="navbar-link">
              Contact
            </a>
            <Link to="/track-project" className="navbar-link">
              Track Project
            </Link>
            <button className="btn btn-primary nav-consult-btn" onClick={startProject}>
              Request Consultation
            </button>
          </div>

          <button
            className="navbar-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`fas ${mobileMenuOpen ? "fa-times" : "fa-bars"}`}></i>
          </button>
        </div>
      </nav>
      {/* Hero Section */}
      <section className="custom-software-hero">
        <div className="custom-software-container">
          <div className="custom-software-breadcrumb">
            <span>Home</span> / <span>Services</span> /{" "}
            <span className="font-semibold">Custom Software Development</span>
          </div>
          <h1 className="custom-software-hero-title">
            Custom Software Development
          </h1>
          <p className="custom-software-hero-subtitle">
            Build software aligned to your operating model. From enterprise
            systems to specialized internal platforms, we deliver custom
            software development South African teams rely on.
          </p>
        </div>
      </section>

      {/* Overview Section */}
      <section className="custom-software-overview">
        <div className="custom-software-container">
          <div className="custom-software-grid-2col">
            <div>
              <h2 className="custom-software-section-title">
                Software Built Around Your Business
              </h2>
              <p className="custom-software-section-subtitle">
                Off-the-shelf products often require teams to adapt around tool
                limitations. Custom software is built around your workflows,
                governance, and reporting requirements to improve operational
                efficiency across Johannesburg and the wider South African
                market.
              </p>
              <p className="custom-software-section-subtitle">
                Whether you need to automate complex workflows, integrate legacy
                systems, or build entirely new platforms, our team has the
                expertise to deliver powerful, reliable software systems while
                serving businesses across Johannesburg.
              </p>
            </div>
            <div className="custom-software-why-box">
              <h3 className="custom-software-why-title">
                Why Custom Software?
              </h3>
              <div className="custom-software-why-list">
                {whyChooseUs.map((benefit, idx) => (
                  <div key={idx} className="custom-software-why-item">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="custom-software-why-text">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Showcase Section */}
      <section className="custom-software-showcase">
        <div className="custom-software-container">
          <h2 className="custom-software-showcase-title">
            Enterprise Software, Built with Precision
          </h2>
          <p className="custom-software-showcase-subtitle">
            From planning to production, we design software systems that are
            maintainable, secure, and ready to support long-term growth.
          </p>
          <div className="custom-software-showcase-grid">
            {projectShowcase.map((item, idx) => (
              <article key={idx} className="custom-software-showcase-card">
                <img src={item.src} alt={item.title} className="custom-software-showcase-image" />
                <div className="custom-software-showcase-overlay">
                  <span className="custom-software-showcase-tag">{item.tag}</span>
                  <h3 className="custom-software-showcase-card-title">{item.title}</h3>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="custom-software-benefits">
        <div className="custom-software-container">
          <h2 className="custom-software-benefits-title">
            The Custom Software Advantage
          </h2>
          <div className="custom-software-benefits-grid">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="custom-software-benefit-card">
                <div className="custom-software-benefit-icon">
                  {benefit.icon}
                </div>
                <h3 className="custom-software-benefit-title">
                  {benefit.title}
                </h3>
                <p className="custom-software-benefit-description">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="custom-software-solutions">
        <div className="custom-software-container">
          <h2 className="custom-software-solutions-title">
            Solutions We Build
          </h2>
          <p className="custom-software-solutions-subtitle">
            Custom software delivery services aligned to core business needs
          </p>
          <div className="custom-software-solutions-grid">
            {whatWeOffer.map((item, idx) => (
              <div key={idx} className="custom-software-solution-card">
                <div className="custom-software-solution-icon">{item.icon}</div>
                <h3 className="custom-software-solution-title">{item.title}</h3>
                <p className="custom-software-solution-description">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="custom-software-industries">
        <div className="custom-software-container">
          <h2 className="custom-software-industries-title">
            Industries We Serve
          </h2>
          <p className="custom-software-industries-subtitle">
            Expertise across multiple sectors with deep domain knowledge
          </p>
          <div className="custom-software-industries-grid">
            {industries.map((industry, idx) => (
              <div key={idx} className="custom-software-industry-card">
                <div className="custom-software-industry-icon">
                  {industry.icon}
                </div>
                <h3 className="custom-software-industry-title">
                  {industry.name}
                </h3>
                <p className="custom-software-industry-description">
                  {industry.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="custom-software-technologies">
        <div className="custom-software-container">
          <h2 className="custom-software-technologies-title">
            Our Technology Stack
          </h2>
          <p className="custom-software-technologies-subtitle">
            Enterprise-grade technologies for robust, scalable software
          </p>

          <div className="custom-software-tab-container">
            {Object.keys(technologies).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`custom-software-tab-btn ${
                  activeTab === tab ? "active" : ""
                }`}
              >
                {tab === "languages"
                  ? "Programming Languages"
                  : tab === "frameworks"
                  ? "Frameworks"
                  : "DevOps & Tools"}
              </button>
            ))}
          </div>

          <div className="custom-software-technologies-grid">
            {technologies[activeTab].map((tech, idx) => (
              <div key={idx} className="custom-software-tech-card">
                <div className="custom-software-tech-icon">
                  <img
                    src={tech.logo}
                    alt={tech.name}
                    onError={(e) => {
                      e.target.onerror = null;
                      const color =
                        tech.color ||
                        fallbackPalette[
                          Math.abs(tech.name.split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0)) %
                            fallbackPalette.length
                        ];
                      e.target.src = makeFallbackSvg(tech.name, color);
                    }}
                  />
                </div>
                <span className="custom-software-tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="custom-software-process">
        <div className="custom-software-container">
          <h2 className="custom-software-process-title">
            Our Development Process
          </h2>
          <p className="custom-software-process-subtitle">
            Structured methodology for predictable software delivery
          </p>
          <div className="custom-software-process-steps">
            {process.map((item, idx) => (
              <div key={idx} className="custom-software-process-step">
                <div className="custom-software-step-number">{item.step}</div>
                <h3 className="custom-software-step-title">{item.title}</h3>
                <p className="custom-software-step-description">
                  {item.description}
                </p>
                {idx < process.length - 1 && (
                  <div className="custom-software-process-arrow">
                    <ArrowRight className="w-6 h-6" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="custom-software-packages">
        <div className="custom-software-container">
          <h2 className="custom-software-packages-title">
            Investment Packages
          </h2>
          <p className="custom-software-packages-subtitle">
            Scalable options from small tools to enterprise systems
          </p>
          <div className="custom-software-packages-grid">
            {packages.map((pkg, idx) => (
              <div
                key={idx}
                className={`custom-software-package-card ${
                  pkg.popular ? "popular" : "standard"
                }`}
              >
                {pkg.popular && (
                  <div className="custom-software-popular-badge">
                    MOST POPULAR
                  </div>
                )}
                <h3 className="custom-software-package-name">{pkg.name}</h3>
                <div
                  className={`custom-software-package-price ${
                    pkg.popular ? "popular" : "standard"
                  }`}
                >
                  {pkg.price}
                </div>
                <p
                  className={`custom-software-package-ideal ${
                    pkg.popular ? "popular" : "standard"
                  }`}
                >
                  {pkg.ideal}
                </p>

                <div className="custom-software-features-list">
                  {pkg.features.map((feature, i) => (
                    <div key={i} className="custom-software-feature-item">
                      {feature.included ? (
                        <Check
                          className={`w-5 h-5 flex-shrink-0 ${
                            pkg.popular ? "text-green-300" : "text-green-500"
                          }`}
                        />
                      ) : (
                        <X
                          className={`w-5 h-5 flex-shrink-0 ${
                            pkg.popular ? "text-emerald-300" : "text-slate-300"
                          }`}
                        />
                      )}
                      <span
                        className={`custom-software-feature-text ${
                          feature.included ? "included" : "excluded"
                        } ${pkg.popular ? "popular" : "standard"}`}
                      >
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>

                <button
                  className={`custom-software-package-btn ${
                    pkg.popular ? "popular" : "standard"
                  }`}
                  onClick={startProject}
                >
                  Discuss Requirements
                </button>
              </div>
            ))}
          </div>
          <p className="custom-software-packages-note">
            All packages include source code ownership and comprehensive
            documentation
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="custom-software-cta">
        <div className="custom-software-container">
          <h2 className="custom-software-cta-title">
            Plan Your Next Software Initiative
          </h2>
          <p className="custom-software-cta-subtitle">
            Discuss your roadmap with our team and get a clear delivery plan
            tailored to your business objectives.
          </p>
          <div className="custom-software-cta-buttons">
            <button className="custom-software-cta-btn primary" onClick={startProject}>
              Request Consultation <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>
       <footer className="footer">
        <div className="footer-decoration"></div>
        <div className="footer-decoration"></div>

        <div className="container">
          <div className="footer-grid">
            <div>
              <div className="footer-brand">
                <img src="/TechXM-logo.png" alt="TECHXM logo" className="brand-logo-mark" width="30" height="30" />
              </div>
              <p className="footer-about">
                We design, build, and support secure digital systems for
                organizations that require reliability, scalability, and strong
                delivery governance.
              </p>
              <div className="footer-social">
                <a
                  href="https://www.linkedin.com/company/techxm/"
                  className="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-linkedin"></i>
                </a>
                <a
                  href="https://www.instagram.com/techxm_za/"
                  className="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-instagram"></i>
                </a>
                <a
                  href="https://www.facebook.com/"
                  className="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-facebook"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="footer-heading">Services</h4>
              <ul className="footer-links">
                <li>
                  <a href="/web-development">
                    <i className="fas fa-chevron-right"></i>
                    Web Development
                  </a>
                </li>
                <li>
                  <a href="/mobile-app">
                    <i className="fas fa-chevron-right"></i>
                    Mobile Apps
                  </a>
                </li>
                <li>
                  <a href="/custom-software">
                    <i className="fas fa-chevron-right"></i>
                    Custom Software
                  </a>
                </li>
                <li>
                  <a href="/api-backend">
                    <i className="fas fa-chevron-right"></i>
                    API & Backend
                  </a>
                </li>
                <li>
                  <a href="/maintenance">
                    <i className="fas fa-chevron-right"></i>
                    Maintenance
                  </a>
                </li>
                <li>
                  <a href="/ai-services">
                    <i className="fas fa-chevron-right"></i>
                    AI & Machine Learning
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="footer-heading">Company</h4>
              <ul className="footer-links">
                <li>
                  <a href="/#about">
                    <i className="fas fa-chevron-right"></i>
                    About
                  </a>
                </li>
                <li>
                  <a href="/#faq">
                    <i className="fas fa-chevron-right"></i>
                    FAQ
                  </a>
                </li>
                <li>
                  <a href="/track-project">
                    <i className="fas fa-chevron-right"></i>
                    Track Project
                  </a>
                </li>
                <li>
{/*
                  <a href="/#testimonials">
                    <i className="fas fa-chevron-right"></i>
                    Testimonials
                  </a>
*/}
                </li>
                <li>
                  <a href="/#contact">
                    <i className="fas fa-chevron-right"></i>
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="footer-heading">Contact Info</h4>
              <div className="footer-contact">
                <div className="contact-item">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>Ohenimuri, Walkerville</span>
                </div>
                <div className="contact-item">
                  <i className="fas fa-phone"></i>
                  <a href="tel:0765536048">076 553 6048</a>
                  <span> / </span>
                  <a href="tel:0742686212">074 268 6212</a>
                </div>
                <div className="contact-item">
                  <i className="fas fa-envelope"></i>
                  <a href="mailto:info@techxm.co.za">info@techxm.co.za</a>
                </div>
                <div className="contact-item">
                  <i className="fas fa-clock"></i>
                  <span>Mon - Fri: 9:00 - 18:00</span>
                </div>
              </div>

              <div className="footer-newsletter">
                <p className="newsletter-text">Subscribe to our newsletter</p>
                <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
                  <input
                    type="email"
                    name="newsletterEmail"
                    className="newsletter-input"
                    placeholder="Your email address"
                    required
                  />
                  <button type="submit" className="newsletter-btn">
                    <i className="fas fa-paper-plane"></i>
                  </button>
                </form>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>
              &copy; 2025 TECHXM. All rights reserved. Enterprise Digital
              Solutions.
            </p>
            <div className="footer-legal">
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms-of-service">Terms of Service</a>
              <a href="/cookie-policy">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}








